import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { ProductCard } from './ProductCard';

/**
 * Horizontal product rail.
 *
 * Replaces a fixed 4-up grid: a grid can only ever show four products per section, so
 * the rest of the catalogue was invisible until a shopper opened a category. A rail
 * shows more stock in the same vertical space, which is how storefronts merchandise.
 *
 * Arrows only appear when there is genuinely something to scroll to — a disabled arrow
 * on a rail that fits on screen is just noise.
 */
export function ProductCarousel({ eyebrow, title, viewAllHref, products, loading, skeletonCount = 5 }) {
  const [emblaRef, embla] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 'auto',
    // Native horizontal swipe is what users expect on touch devices; dragging is
    // enabled everywhere and the arrows are the desktop affordance.
    dragFree: false
  });

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const syncArrows = useCallback(() => {
    if (!embla) return;
    setCanPrev(embla.canScrollPrev());
    setCanNext(embla.canScrollNext());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    syncArrows();
    embla.on('select', syncArrows);
    embla.on('reInit', syncArrows);
    return () => {
      embla.off('select', syncArrows);
      embla.off('reInit', syncArrows);
    };
  }, [embla, syncArrows]);

  /**
   * Products arrive after the first paint, so the slide set changes from skeletons to
   * real cards under Embla's feet. Re-measuring explicitly means the arrows reflect the
   * real content — without it the rail can render scrollable but show no arrows.
   */
  useEffect(() => {
    if (embla) embla.reInit();
  }, [embla, products?.length, loading]);

  const hasOverflow = canPrev || canNext;

  // Two slides on the smallest screens, up to five on a wide desktop — the peeking
  // half-card is the cue that the rail scrolls.
  const slideClass =
    'flex-[0_0_72%] sm:flex-[0_0_45%] md:flex-[0_0_33%] lg:flex-[0_0_25%] xl:flex-[0_0_20.5%] min-w-0 pl-4';

  if (!loading && (!products || products.length === 0)) return null;

  return (
    <section className="py-10 md:py-14" data-testid={`carousel-${(title || '').toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            {eyebrow && (
              <span className="text-xs uppercase tracking-widest text-primary font-body font-bold mb-1.5 block">
                {eyebrow}
              </span>
            )}
            <h2 className="font-heading text-2xl md:text-4xl tracking-tight text-secondary">
              {title}
            </h2>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {viewAllHref && (
              <Link
                to={viewAllHref}
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-body font-semibold text-primary hover:gap-2.5 transition-all mr-2"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {hasOverflow && (
              <>
                <button
                  onClick={() => embla?.scrollPrev()}
                  disabled={!canPrev}
                  aria-label="Previous products"
                  className="w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center text-secondary transition-all hover:border-primary hover:text-primary disabled:opacity-30 disabled:hover:border-gray-200 disabled:hover:text-secondary"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => embla?.scrollNext()}
                  disabled={!canNext}
                  aria-label="Next products"
                  className="w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center text-secondary transition-all hover:border-primary hover:text-primary disabled:opacity-30 disabled:hover:border-gray-200 disabled:hover:text-secondary"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* -ml-4 cancels the pl-4 gutter on the first slide so the rail aligns with the heading */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-4">
            {loading
              ? [...Array(skeletonCount)].map((_, idx) => (
                <div key={idx} className={slideClass}>
                  <div className="aspect-square bg-surface rounded-3xl animate-pulse" />
                  <div className="h-3 bg-surface rounded-full animate-pulse mt-3 w-2/3" />
                  <div className="h-3 bg-surface rounded-full animate-pulse mt-2 w-1/3" />
                </div>
              ))
              : products.map((product, idx) => (
                <div key={`${product.product_id || 'p'}-${idx}`} className={slideClass}>
                  <ProductCard product={product} />
                </div>
              ))}
          </div>
        </div>

        {viewAllHref && (
          <div className="sm:hidden mt-5">
            <Link
              to={viewAllHref}
              className="inline-flex items-center gap-1.5 text-sm font-body font-semibold text-primary"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
